import apiClient from '../config/api';

export interface NotOkCalculation {
  calculationId: number;
  apptId: string;
  hospitalUuid: string;
  hospitalName: string;
  hospitalCode: string;
  agreementId: number;
  agreementVersion: number;
  hospitalShare: number;
  arAp: number;
  validationStatus: string;
  errorCategory: string;
  remarks: string;
  calculatedBy: string;
  calculatedAt: string;
  executionTimeMs: number;
  operatorName: string;
  operatorEmail: string;
  inputParameterValues: Record<string, unknown>;
  calculatedParameterValues: Record<string, unknown>;
}

export interface NotOkCalculationsResponse {
  calculations: NotOkCalculation[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface CalculationErrorEntry {
  errorId: number;
  calculationId: number;
  apptId: string;
  errorType: string;
  errorMessage: string;
  failedParameter: string | null;
  failedFormula: string | null;
  stackTrace: string | null;
  occurredAt: string;
  hospitalUuid: string | null;
  hospitalName: string | null;
  hospitalCode: string | null;
  agreementId: number | null;
  calculatedBy: string | null;
  operatorName: string | null;
  operatorEmail: string | null;
}

export interface CalculationErrorsResponse {
  errors: CalculationErrorEntry[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface ListNotOkParams {
  hospitalUuid?: string;
  errorCategory?: string;
  dateFrom?: string;
  dateTill?: string;
  page?: number;
  limit?: number;
}

function mapNotOkCalculation(raw: Record<string, unknown>): NotOkCalculation {
  return {
    calculationId: raw.calculation_id as number,
    apptId: raw.appt_id as string,
    hospitalUuid: raw.hospital_uuid as string,
    hospitalName: raw.hospital_name as string,
    hospitalCode: raw.hospital_code as string,
    agreementId: raw.agreement_id as number,
    agreementVersion: raw.agreement_version as number,
    hospitalShare: raw.hospital_share as number,
    arAp: raw.ar_ap as number,
    validationStatus: raw.validation_status as string,
    errorCategory: raw.error_category as string,
    remarks: raw.remarks as string,
    calculatedBy: raw.calculated_by as string,
    calculatedAt: raw.calculated_at as string,
    executionTimeMs: raw.execution_time_ms as number,
    operatorName: raw.operator_name as string,
    operatorEmail: raw.operator_email as string,
    inputParameterValues: (raw.input_parameter_values as Record<string, unknown>) || {},
    calculatedParameterValues: (raw.calculated_parameter_values as Record<string, unknown>) || {},
  };
}

export async function listNotOkCalculations(
  params: ListNotOkParams = {}
): Promise<NotOkCalculationsResponse> {
  const query: Record<string, string> = {};
  if (params.hospitalUuid) query.hospitalUuid = params.hospitalUuid;
  if (params.errorCategory) query.errorCategory = params.errorCategory;
  if (params.dateFrom) query.dateFrom = params.dateFrom;
  if (params.dateTill) query.dateTill = params.dateTill;
  if (params.page) query.page = String(params.page);
  if (params.limit) query.limit = String(params.limit);

  const response = await apiClient.get('/api/admin/not-ok-calculations', { params: query });
  const data = response.data;
  return {
    calculations: (data.calculations || []).map(mapNotOkCalculation),
    pagination: data.pagination,
  };
}

export interface ListCalcErrorsParams {
  errorType?: string;
  dateFrom?: string;
  dateTill?: string;
  page?: number;
  limit?: number;
}

function mapCalcError(raw: Record<string, unknown>): CalculationErrorEntry {
  return {
    errorId: raw.error_id as number,
    calculationId: raw.calculation_id as number,
    apptId: raw.appt_id as string,
    errorType: raw.error_type as string,
    errorMessage: raw.error_message as string,
    failedParameter: (raw.failed_parameter as string) ?? null,
    failedFormula: (raw.failed_formula as string) ?? null,
    stackTrace: (raw.stack_trace as string) ?? null,
    occurredAt: raw.occurred_at as string,
    hospitalUuid: (raw.hospital_uuid as string) ?? null,
    hospitalName: (raw.hospital_name as string) ?? null,
    hospitalCode: (raw.hospital_code as string) ?? null,
    agreementId: (raw.agreement_id as number) ?? null,
    calculatedBy: (raw.calculated_by as string) ?? null,
    operatorName: (raw.operator_name as string) ?? null,
    operatorEmail: (raw.operator_email as string) ?? null,
  };
}

export async function listCalculationErrors(
  params: ListCalcErrorsParams = {}
): Promise<CalculationErrorsResponse> {
  const query: Record<string, string> = {};
  if (params.errorType) query.errorType = params.errorType;
  if (params.dateFrom) query.dateFrom = params.dateFrom;
  if (params.dateTill) query.dateTill = params.dateTill;
  if (params.page) query.page = String(params.page);
  if (params.limit) query.limit = String(params.limit);

  const response = await apiClient.get('/api/admin/calculation-errors', { params: query });
  const data = response.data;
  return {
    errors: (data.errors || []).map(mapCalcError),
    pagination: data.pagination,
  };
}
